import { Wheat, Tractor, Cloud, BarChart3, Leaf, Droplets } from 'lucide-react';
import { Product } from './types';

export const agricultureProducts: Product[] = [
  {
    id: 'agricultural-supply-chain-platform',
    title: 'Agricultural Supply Chain Platform',
    subtitle: 'Enterprise farm-to-market connectivity with blockchain traceability',
    description: 'Advanced supply chain management platform with blockchain traceability, multi-currency support, and real-time commodity exchange integration. Outperforming John Deere Operations Center and AgriDigital with comprehensive compliance automation.',
    icon: Wheat,
    image: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Agriculture & Farming',
    status: 'Available',
    features: [
      'Blockchain traceability & product authentication with RFID/NFC integration',
      'Real-time cold chain monitoring with IoT temperature/humidity sensors',
      'Multi-currency support & international trade documentation automation',
      'Carbon credit tracking & trading platform integration',
      'Predictive demand forecasting with machine learning algorithms',
      'Direct integration with commodity exchanges (LIFFE, CME, CBOT)',
      'Advanced inventory management with lot tracking & batch control',
      'DEFRA compliance automation & regulatory reporting',
      'GPS-enabled logistics optimization & route planning',
      'Quality control workflows with photographic evidence capture',
      'Farmer marketplace with direct-to-consumer sales channels',
      'Real-time price analytics & market intelligence dashboard',
      'Supply chain financing & payment processing integration',
      'Environmental impact tracking & sustainability reporting',
      'Multi-stakeholder collaboration portal with secure document sharing'
    ],
    pricing: { setup: 40000, monthly: 1840 },
    color: 'from-green-500 to-emerald-600',
    businessTypes: ['#agriculture', '#supply-chain', '#farming', '#logistics', '#traceability', '#blockchain', '#compliance']
  },
  {
    id: 'precision-farming-solution',
    title: 'Precision Farming Solution',
    subtitle: 'Advanced AI-driven precision agriculture with equipment integration',
    description: 'Next-generation precision farming platform with variable rate technology, multi-spectral analysis, and seamless integration with John Deere, Case IH, and New Holland equipment. Outperforming Climate FieldView and Trimble Ag with superior yield prediction accuracy.',
    icon: Tractor,
    image: 'https://images.unsplash.com/photo-1473773508845-188df298d2d1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Agriculture & Farming',
    status: 'Available',
    features: [
      'Variable rate seeding/planting with prescription map generation',
      'Multi-spectral & hyperspectral satellite imagery analysis (Planet Labs integration)',
      'NDVI, NDRE, and EVI vegetation indices with temporal analysis',
      'Soil compaction mapping using penetration resistance data',
      'Direct integration with John Deere, Case IH, New Holland equipment APIs',
      'Weather station integration (Davis Instruments, Campbell Scientific)',
      'Machine learning yield prediction with 97% accuracy confidence',
      'Automated fertilizer & lime prescription maps with variable rate application',
      'Pest pressure modeling with Economic Injury Level (EIL) calculations',
      'Crop growth stage identification using computer vision algorithms',
      'Field boundary detection with sub-meter GPS accuracy',
      'Equipment telematics integration for performance optimization',
      'Drone flight planning with automated data processing workflows',
      'Harvest optimization with combine harvester integration & grain quality mapping'
    ],
    pricing: { setup: 44800, monthly: 2061 },
    color: 'from-yellow-500 to-amber-600',
    businessTypes: ['#precision-farming', '#data-analytics', '#agtech', '#sensors', '#automation', '#variable-rate']
  },
  {
    id: 'farm-management-software',
    title: 'Farm Management Software',
    subtitle: 'Enterprise multi-farm operations with financial integration',
    description: 'Comprehensive farm management platform with multi-entity support, GAAP compliance, and seamless integration with QuickBooks, SAP, and Oracle. Exceeding Granular and AgriWebb capabilities with advanced contract farming management.',
    icon: Leaf,
    image: 'https://images.unsplash.com/photo-1625246333195-78d9c38ad449?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Agriculture & Farming',
    status: 'Available',
    features: [
      'Multi-entity & multi-farm management with centralized dashboard',
      'Advanced crop planning with rotation optimization & soil health tracking',
      'Livestock management with breeding records, health tracking & feed optimization',
      'Contract farming management with grower agreements & performance tracking',
      'GAAP-compliant financial reporting with profit/loss by field & enterprise',
      'Seamless QuickBooks, SAP, Oracle ERP integration with automated sync',
      'Harvest management with grain marketing & forward contract tracking',
      'Advanced inventory management with lot tracking & expiration monitoring',
      'Mobile workforce management with geofencing & time tracking',
      'Equipment maintenance scheduling with predictive failure analysis',
      'Labor cost allocation by field, crop, and activity with payroll integration',
      'Comprehensive compliance tracking for organic, environmental & safety regulations',
      'Real-time field activity tracking with GPS-enabled mobile applications',
      'Advanced reporting with customizable dashboards & automated scheduled reports'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-green-500 to-teal-600',
    businessTypes: ['#farm-management', '#agriculture', '#operations', '#planning', '#reporting', '#financial']
  },
  {
    id: 'irrigation-management-system',
    title: 'Irrigation Management System',
    subtitle: 'Intelligent water management with ET calculations & VRI technology',
    description: 'Advanced irrigation management with evapotranspiration calculations, variable rate irrigation, and water rights management. Surpassing Lindsay FieldNET and Netafim with comprehensive fertigation control and leak detection capabilities.',
    icon: Droplets,
    image: 'https://images.unsplash.com/photo-1574323347407-f5e1ad6d020b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Agriculture & Farming',
    status: 'Available',
    features: [
      'Advanced ET (evapotranspiration) calculation with CIMIS & AgWeatherNet integration',
      'Variable Rate Irrigation (VRI) with prescription map generation & pivot control',
      'Comprehensive soil moisture sensor network management (Decagon, Watermark)',
      'Real-time weather station integration with forecast-based irrigation scheduling',
      'Water rights & allocation management with regulatory compliance tracking',
      'Fertigation control with nutrient injection timing & concentration management',
      'Advanced leak detection with pressure monitoring & flow anomaly alerts',
      'Pump station automation with energy optimization & variable frequency drives',
      'Mobile valve control with GPS-based zone management & remote operation',
      'Comprehensive water usage tracking with cost analysis & efficiency reporting',
      'Integration with satellite-based crop stress monitoring for irrigation timing',
      'Automated system diagnostics with predictive maintenance scheduling',
      'Multi-crop irrigation scheduling with growth stage consideration & water requirements'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-blue-500 to-cyan-600',
    businessTypes: ['#irrigation', '#water-management', '#sensors', '#automation', '#conservation', '#fertigation']
  },
  {
    id: 'crop-monitoring-analytics',
    title: 'Crop Monitoring & Analytics',
    subtitle: 'AI-powered crop intelligence with computer vision & yield prediction',
    description: 'Advanced crop monitoring platform with computer vision disease detection, 97% accurate yield prediction, and integration with Planet Labs satellite data. Exceeding Taranis AI and EOSDA capabilities with comprehensive stress detection algorithms.',
    icon: BarChart3,
    image: 'https://images.unsplash.com/photo-1477511801984-4ad318ed9846?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Agriculture & Farming',
    status: 'Available',
    features: [
      'Computer vision disease identification with pathogen-specific diagnostics',
      'Advanced pest detection & population monitoring with Economic Threshold modeling',
      'Machine learning yield prediction with 97% accuracy & confidence intervals',
      'Multi-source satellite imagery integration (Planet Labs, Sentinel, Landsat)',
      'Comprehensive stress detection (water, nutrient, heat, disease, pest)',
      'Growth stage identification using phenology models & thermal imaging',
      'Harvest timing optimization with quality prediction algorithms',
      'Spectral analysis for grain quality assessment & protein content prediction',
      'Drone-based scouting with automated flight path planning & image processing',
      'Real-time field alerts with mobile notifications & GPS-based recommendations',
      'Crop emergence monitoring with stand count analysis & replant recommendations',
      'Weather impact assessment with risk modeling for crop insurance claims',
      'Integration with crop models (DSSAT, APSIM) for comprehensive growth simulation',
      'Comparative field analysis with benchmarking against regional & historical data'
    ],
    pricing: { setup: 35200, monthly: 1619 },
    color: 'from-purple-500 to-indigo-600',
    businessTypes: ['#crop-monitoring', '#analytics', '#ai', '#satellite', '#drones', '#computer-vision']
  },
  {
    id: 'environmental-monitoring-system',
    title: 'Environmental Monitoring System',
    subtitle: 'Advanced environmental compliance & sustainability monitoring',
    description: 'Comprehensive environmental monitoring platform with multi-parameter sensor networks, automated regulatory reporting, and carbon sequestration measurement. Exceeding industry standards with EPA-compliant air quality monitoring and biodiversity impact assessment.',
    icon: Cloud,
    image: 'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Agriculture & Farming',
    status: 'Available',
    features: [
      'Multi-parameter air quality monitoring (PM2.5, PM10, NO2, SO2, O3, CO)',
      'Advanced soil analysis with heavy metal & contamination detection',
      'Comprehensive water quality testing with automated pathogen detection',
      'Weather station networks with microclimate monitoring & frost prediction',
      'Biodiversity impact assessment with species tracking & habitat monitoring',
      'Carbon sequestration measurement using soil organic carbon analysis',
      'Automated EPA & state environmental agency regulatory reporting',
      'Sustainability certification support (LEED, Organic, Carbon Neutral)',
      'Greenhouse gas emissions tracking (CO2, CH4, N2O) with Scope 1-3 reporting',
      'Noise pollution monitoring with agricultural activity correlation',
      'Pesticide residue monitoring with drift detection & neighbor notifications',
      'Wildlife habitat monitoring with camera traps & acoustic sensors',
      'Energy consumption tracking with renewable energy integration analysis',
      'Comprehensive environmental impact reporting with third-party verification support'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-teal-500 to-green-600',
    businessTypes: ['#environmental', '#monitoring', '#sustainability', '#soil', '#weather', '#compliance', '#carbon-tracking']
  }
];
